<template>
  <div class="cart-container">
    <template v-if="!collapsed">
      <div class="sidebar-top-row">
        <div class="sidebar-title-left">
          <button class="sidebar-expand-btn" type="button" @click="$emit('toggle-expand')">
            <span class="sidebar-expand-icon">{{ expanded ? '»' : '«' }}</span>
          </button>
          <span class="sidebar-subtotal-label">Subtotal:</span>
        </div>
        <span class="sidebar-subtotal-value">${{ formatCurrency(subtotal) }}</span>
      </div>
      <div class="sidebar-tabs">
        <button
          type="button"
          class="sidebar-tab-btn"
          :class="{ active: activeSidebarTab === 'products' }"
          @click="activeSidebarTab = 'products'"
        >
          <img src="@/assets/newOrder/vibrant-product-selected.svg" alt="Products" />
          <span>Products</span>
        </button>
        <button
          type="button"
          class="sidebar-tab-btn"
          :class="{ active: activeSidebarTab === 'tubes' }"
          @click="activeSidebarTab = 'tubes'"
        >
          <img src="@/assets/orderTest/order-page/promoPanel/icon-test-tubes.svg" alt="Tubes" />
          <span>Tubes</span>
        </button>
      </div>
      <div class="sidebar-items">
        <template v-if="activeSidebarTab === 'products'">
          <template v-if="filteredSidebarGroups.length">
            <div v-for="group in filteredSidebarGroups" :key="group.key" class="sidebar-group">
              <div class="sidebar-group-title">{{ group.label }}</div>
              <div v-for="item in group.items" :key="item.id" class="sidebar-item-row">
                <div class="sidebar-item-main">
                  <div class="sidebar-item-name" :title="item.name">{{ item.name }}</div>
                  <div v-if="sidebarItemMeta(item)" class="sidebar-item-meta">{{ sidebarItemMeta(item) }}</div>
                </div>
                <div class="sidebar-item-price-group">
                  <div class="sidebar-item-price">${{ formatCurrency(item.quantity * item.unitPrice) }}</div>
                  <button
                    v-if="expanded"
                    type="button"
                    class="sidebar-item-remove-btn"
                    aria-label="Remove item"
                    @click="removeSidebarItem(item.id)"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="sidebar-empty">No products.</div>
        </template>
        <template v-else>
          <template v-if="tubeSidebarGroups.length">
            <div v-for="group in tubeSidebarGroups" :key="group.key" class="sidebar-group sidebar-tube-group">
              <div class="sidebar-group-title">{{ group.label }}</div>
              <template v-if="expanded">
                <div class="tube-table-header">
                  <span>Tube</span>
                  <span>Specimen Info</span>
                  <span>Qty</span>
                  <span>Volume Required</span>
                </div>
                <div v-for="tube in group.items" :key="tube.id" class="tube-row">
                  <div class="tube-image-col">
                    <img
                      class="tube-image"
                      :src="tube.image || require('@/assets/orderTest/order-page/promoPanel/icon-test-tubes.svg')"
                      :alt="tube.name"
                    >
                  </div>
                  <div class="tube-name-col">
                    <div class="tube-name">{{ tube.name }}</div>
                    <div v-if="tube.specimenInfo" class="tube-specimen">{{ tube.specimenInfo }}</div>
                  </div>
                  <div class="tube-qty">{{ tube.qty }}</div>
                  <div class="tube-volume">{{ tube.volume }}</div>
                </div>
              </template>
              <template v-else>
                <div v-for="tube in group.items" :key="tube.id" class="tube-row compact">
                  <div class="tube-name-col">
                    <div class="tube-name">{{ tube.name }}</div>
                    <div v-if="tube.specimenInfo" class="tube-specimen">{{ tube.specimenInfo }}</div>
                  </div>
                  <div class="tube-qty">{{ tube.qty }}</div>
                  <div class="tube-volume">{{ tube.volume }}</div>
                </div>
              </template>
            </div>
          </template>
          <div v-else class="sidebar-empty">No tubes.</div>
        </template>
      </div>
    </template>

    <template v-else>
      <div class="subtotal-header flex justify-between items-center pb-4 border-b border-dashed border-gray-200">
        <span class="step-title">Step 1: Select Tests</span>
        <Button color-type="none-stroke" @click="$emit('edit')">Edit</Button>
      </div>
      <div class="cart-scrollable-content">
        <div class="items-table">
          <div
            class="grid table-header text-sm px-3 py-2 items-center"
            style="grid-template-columns: 340px 80px 75px 105px 75px 63px;"
            :class="collapsed ? 'gap-16' : 'gap-4'"
          >
            <div>Items</div>
            <div class="text-center">Count</div>
            <div class="text-center">Unit Price</div>
            <div class="text-center">Revenue</div>
            <div class="text-center ml-4">Price</div>
            <div></div>
          </div>
          <div class="border-b border-dashed border-gray-200">
            <div v-for="item in cartItems" :key="item.id" class="item-row-container">
              <div
                class="grid item-row items-center px-3 py-3"
                style="grid-template-columns: 340px 80px 75px 105px 75px 63px;"
                :class="collapsed ? 'gap-16 bg-[#F9FBFD]' : 'gap-4 bg-white'"
              >
                <div class="flex items-center cursor-pointer" @click="toggleSubtests(item.id)">
                  <img :src="isExpanded(item.id) ? require('@/assets/newOrder/up.svg') : require('@/assets/newOrder/down.svg')" alt="Toggle" class="w-4 h-4 mr-2">
                  <span>{{ item.name }} - {{ item.subtests.length }} subtests</span>
                </div>
                <div class="flex items-center justify-center">
                  <span>X {{ item.quantity }}</span>
                </div>
                <div class="text-center">
                  ${{ formatCurrency(item.unitPrice) }}
                </div>
                <div class="text-center flex items-center justify-center">
                  ${{ formatCurrency(item.revenue) }}
                  <Button color-type="none-stroke" @click="item.revenueEditVisible = true">Edit</Button>
                </div>
                <div class="text-right">
                  ${{ formatCurrency(item.quantity * item.unitPrice) }}
                </div>
              </div>
              <div v-if="isExpanded(item.id)" class="subtests-container px-6 rounded-b-lg -mt-1">
                <ul class="list-disc list-inside text-sm">
                  <li v-for="(subtest, subIndex) in item.subtests" :key="subIndex">{{ subtest }}</li>
                </ul>
              </div>
            </div>
          </div>
          <div v-if="hasPriceSummary" class="price-summary-block mt-4 mr-12" :class="{ 'is-loading': summaryLoading }">
            <div
              v-for="line in visibleSummaryLines"
              :key="line.label"
              class="price-summary-line"
            >
              <span class="price-summary-label">{{ line.label }}</span>
              <span class="price-summary-value">${{ formatCurrency(line.value) }}</span>
            </div>
          </div>
          <p class="subtotal-header flex justify-end items-center gap-6 mr-12 h-10 mt-4">
            {{ subtotalLabel }}
            <span class="cart-subtotal">${{ formatCurrency(finalSubtotalValue) }}</span>
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Button from '@/NewLibrary/Button.vue'
export default {
  components: {
    Button
  },
  name: 'NewOrderCartItems',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    hovered: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default: false
    },
    priceSummary: {
      type: Object,
      default: () => ({
        lines: [],
        estimateTotal: null,
      }),
    },
    summaryLoading: {
      type: Boolean,
      default: false,
    },
    subtotalLabel: {
      type: String,
      default: 'Cart Subtotal:',
    }
  },
  emits: ['update:items', 'edit', 'toggle-expand', 'remove-item'],
  data() {
    return {
      cartItems: [],
      expandedItems: new Set(),
      activeSidebarTab: 'products',
    };
  },
  computed: {
    subtotal() {
      return this.cartItems.reduce((total, item) => total + (item.quantity * item.unitPrice), 0);
    },
    hasPriceSummary() {
      return this.visibleSummaryLines.length > 0;
    },
    visibleSummaryLines() {
      if (!this.priceSummary || !Array.isArray(this.priceSummary.lines)) {
        return [];
      }
      return this.priceSummary.lines.filter((line) => Number(line.value) > 0);
    },
    finalSubtotalValue() {
      const estimateTotal = Number(this.priceSummary?.estimateTotal);
      if (Number.isFinite(estimateTotal)) {
        return estimateTotal;
      }
      return this.subtotal;
    },
    sidebarGroups() {
      const groupMap = new Map();
      const groupOrder = ['vibrant', 'practice', 'bundle', 'program', 'quick_bill', 'other'];
      const groupLabelMap = {
        vibrant: 'Vibrant Product',
        practice: 'Practice Product',
        bundle: 'Bundle',
        program: 'Program',
        quick_bill: 'Quick Bill',
        other: 'Item',
      };

      this.cartItems.forEach(item => {
        const key = this.resolveGroupKey(item);
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            key,
            label: groupLabelMap[key] || 'Item',
            items: [],
          });
        }
        groupMap.get(key).items.push(item);
      });

      return groupOrder
        .filter(key => groupMap.has(key))
        .map(key => groupMap.get(key));
    },
    filteredSidebarGroups() {
      if (this.activeSidebarTab === 'tubes') {
        return [];
      }
      return this.sidebarGroups;
    },
    tubeSidebarGroups() {
      const groups = {
        blood: {
          key: 'blood',
          label: 'BLOOD KIT',
          items: [],
        },
        non_blood: {
          key: 'non_blood',
          label: 'NON-BLOOD KIT',
          items: [],
        },
      };

      this.cartItems.forEach(item => {
        const tubes = this.getTubeEntriesForItem(item);
        tubes.forEach((tube, index) => {
          const normalized = this.normalizeTubeEntry(item, tube, index);
          if (normalized.groupKey === 'blood') {
            groups.blood.items.push(normalized);
            return;
          }
          groups.non_blood.items.push(normalized);
        });
      });

      return [groups.blood, groups.non_blood].filter(group => group.items.length > 0);
    }
  },
  watch: {
    items: {
      immediate: true,
      deep: true,
      handler(newItems) {
        this.cartItems = JSON.parse(JSON.stringify(newItems)).map(item => ({
             ...item,
             quantity: item.quantity || 1,
             revenue: item.revenue || 0,
             unitPrice: item.price,
             // Use real subtests array if provided; otherwise, fallback to empty array or generate simple placeholders if numeric
             subtests: Array.isArray(item.subtests)
               ? item.subtests
               : (typeof item.subtests === 'number'
                  ? Array.from({ length: item.subtests }, (_, i) => `Subtest ${i + 1}`)
                  : []),
             revenueEditVisible: false
        }));
      }
    }
  },
  methods: {
    increment(item) {
      item.quantity++;
      this.emitUpdate();
    },
    decrement(item) {
      item.quantity--;
      if (item.quantity <= 0) {
        this.cartItems = this.cartItems.filter(i => i.id !== item.id);
      }
      this.emitUpdate();
    },
    updateRevenue(item) {
        item.revenueEditVisible = false;
        this.emitUpdate();
    },
    emitUpdate() {
        this.$emit('update:items', this.cartItems);
    },
    toggleSubtests(itemId) {
      if (this.expandedItems.has(itemId)) {
        this.expandedItems.delete(itemId);
      } else {
        this.expandedItems.add(itemId);
      }
      this.$forceUpdate(); // To react to Set changes
    },
    isExpanded(itemId) {
      return this.expandedItems.has(itemId);
    },
    resolveGroupKey(item) {
      const key = this.normalizeGroupKey(item?.tab);
      if (key !== 'other') {
        return key;
      }
      return this.normalizeGroupKey(item?.category || item?.group || item?.source);
    },
    normalizeGroupKey(tab) {
      const key = (tab || '').toString().toLowerCase();
      if (key.includes('vibrant')) {
        return 'vibrant';
      }
      if (key.includes('practice')) {
        return 'practice';
      }
      if (key.includes('bundle') || key.includes('shortcut')) {
        return 'bundle';
      }
      if (key.includes('program')) {
        return 'program';
      }
      if (key.includes('quick')) {
        return 'quick_bill';
      }
      if (['vibrant', 'practice', 'bundle', 'program', 'quick_bill'].includes(key)) {
        return key;
      }
      return 'other';
    },
    sidebarItemMeta(item) {
      if (Array.isArray(item.subtests) && item.subtests.length > 0) {
        return `Include ${item.subtests.length} tests`;
      }
      if (item.includeText) {
        return item.includeText;
      }
      return '';
    },
    getTubeEntriesForItem(item) {
      if (Array.isArray(item.tubes)) {
        return item.tubes;
      }
      if (Array.isArray(item.originalItem?.tubes)) {
        return item.originalItem.tubes;
      }
      if (Array.isArray(item.originalItem?.sampleTubes)) {
        return item.originalItem.sampleTubes;
      }
      return [];
    },
    normalizeTubeEntry(item, tube, index) {
      const name = tube.name || tube.tube_name || tube.tubeType || item.name || 'Tube';
      const specimenInfo = tube.specimenInfo || tube.specimen_info || tube.sample_info || '';
      const qty = Number(tube.qty ?? tube.quantity ?? 1);
      const volume = tube.volumeRequired || tube.required_volume || tube.volume || 'N/A';
      const typeToken = `${tube.category || ''} ${tube.type || ''} ${name}`.toLowerCase();
      const groupKey = typeToken.includes('blood') ? 'blood' : 'non_blood';

      return {
        id: `${item.id}-tube-${index}`,
        name,
        specimenInfo,
        qty: Number.isFinite(qty) ? qty : 1,
        volume,
        image: tube.image || tube.image_url || '',
        groupKey,
      };
    },
    removeSidebarItem(itemId) {
      const targetItem = this.cartItems.find(item => item.id === itemId);
      if (targetItem) {
        this.$emit('remove-item', targetItem);
      }
      this.cartItems = this.cartItems.filter(item => item.id !== itemId);
      this.emitUpdate();
    },
    formatCurrency(value) {
        if (typeof value !== 'number') {
            return '0.00';
        }
        return value.toFixed(2);
    }
  }
};
</script>

<style scoped>
.step-title {
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
  letter-spacing: 0.15px;
  color: var(--TrueBlack, #333);
}

.cart-container {
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border-radius: 8px;
  width: 100%;
  overflow: hidden;
}

.sidebar-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px dashed #c4c4c4;
  padding-bottom: 8px;
}

.sidebar-title-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sidebar-expand-btn {
  width: 28px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: #004879;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.sidebar-expand-icon {
  font-size: 16px;
  line-height: 1;
  font-weight: 700;
}

.sidebar-subtotal-label {
  color: #004879;
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
}

.sidebar-subtotal-value {
  color: #004879;
  font-size: 24px;
  font-weight: 400;
  line-height: 36px;
}

.sidebar-tabs {
  display: flex;
  border-bottom: 1px solid #e6e6e6;
  margin-top: 8px;
}

.sidebar-tab-btn {
  flex: 1;
  height: 32px;
  border: none;
  border-bottom: 1px solid #e6e6e6;
  background: #fff;
  color: #666;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.1px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px 8px;
}

.sidebar-tab-btn img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.sidebar-tab-btn.active {
  color: #004879;
  border-bottom: 2px solid #004879;
}

.sidebar-items {
  margin-top: 10px;
  border-bottom: 1px dashed #c4c4c4;
  padding-bottom: 4px;
  overflow-y: auto;
  overflow-x: hidden;
}

.sidebar-group {
  margin-bottom: 14px;
}

.sidebar-group:last-child .sidebar-item-row:last-child {
  border-bottom: none;
}

.sidebar-group-title {
  color: #c4c4c4;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  letter-spacing: 0.25px;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.sidebar-item-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  min-height: 20px;
}

.sidebar-item-main {
  flex: 1;
  min-width: 0;
}

.sidebar-item-name {
  color: #333;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-tab-btn:not(.active) img {
  filter: grayscale(1);
  opacity: 0.7;
}

.sidebar-item-meta {
  color: #666;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.2px;
  margin-top: 0;
}

.sidebar-item-price {
  color: #333;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  white-space: nowrap;
  text-align: right;
}

.sidebar-item-price-group {
  display: inline-flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 4px;
}

.sidebar-item-remove-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #d10000;
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

.sidebar-item-remove-btn:hover {
  background-color: rgba(209, 0, 0, 0.08);
  color: #b00000;
}

.sidebar-empty {
  color: #666;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  padding: 8px 0;
}

.sidebar-tube-group {
  margin-bottom: 16px;
  overflow-x: hidden;
}

.tube-table-header {
  display: grid;
  grid-template-columns: 68px 1fr 42px 98px;
  color: #999;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.2px;
  border-bottom: 1px dashed #c4c4c4;
  padding: 2px 6px 6px;
  margin-bottom: 6px;
}

.tube-row {
  display: grid;
  grid-template-columns: 68px 1fr 42px 98px;
  column-gap: 8px;
  align-items: center;
  padding: 8px 6px;
}

.tube-row.compact {
  grid-template-columns: 1fr 32px 80px;
  padding: 4px 4px;
}

.tube-image-col {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tube-image {
  width: 24px;
  height: 48px;
  object-fit: contain;
}

.tube-row.compact .tube-image-col,
.tube-row.compact .tube-image {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

.tube-name-col {
  min-width: 0;
}

.tube-name {
  color: #333;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

.tube-row.compact .tube-name {
  font-size: 14px;
}

.tube-specimen {
  color: #4a4a4a;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin-top: 2px;
}

.tube-row.compact .tube-specimen {
  font-size: 12px;
  line-height: 16px;
}

.tube-qty,
.tube-volume {
  color: #333;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  text-align: right;
}

.cart-scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  padding-top: 8px;
}

.cart-scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.cart-scrollable-content::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.cart-scrollable-content::-webkit-scrollbar-track {
  background: transparent;
}

/* Optional: prettier scrollbars */
.cart-wrapper::-webkit-scrollbar {
  width: 6px;
}
.cart-wrapper::-webkit-scrollbar-thumb {
  border-radius: 4px;
}
.cart-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.quantity-btn {
  background-color: #004879;
  color: white;
  border: none;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quantity-display {
  color: var(--SecondaryBlack, #666);
  text-align: center;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 11.321px; /* 113.208% */
  letter-spacing: 0.071px;
  margin: 0 8px;
}

.subtests-container {
    margin-left: 20px;
    margin-right: 20px;
    margin-bottom: 10px;
}

.item-row {
  min-height: 56px; /* match a consistent row height */
  display: grid;
  align-items: center;
}

.subtotal-header {
  color: var(--TrueBlack, #333);
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
  letter-spacing: 0.15px;
}

.cart-subtotal {
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 36px;
  letter-spacing: 0.15px;
}

.price-summary-block {
  margin-left: auto;
  width: 340px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.price-summary-line {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  font-size: 14px;
  line-height: 20px;
  color: #333;
}

.price-summary-label {
  text-align: left;
}

.price-summary-value {
  text-align: right;
  min-width: 72px;
}

.price-summary-block.is-loading {
  opacity: 0.6;
}
  
</style>
